"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { Eye, EyeOff, Loader2, Store, ShieldCheck, Phone } from "lucide-react";
import { SocialProviderButton, type SocialProvider } from "@/components/auth/SocialProviderButton";
import { notifySessionChanged } from "@/lib/auth/session";
import { authApi } from "@/lib/api/auth";

const API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

type LoginMode = "email" | "phone";

export default function LoginPage() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const nextUrl = searchParams?.get('next') || (typeof window !== 'undefined' ? localStorage.getItem('s360_return_url') : null) || null;
    const [loginMode, setLoginMode] = useState<LoginMode>("phone");
    const [loginValue, setLoginValue] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [socialLoading, setSocialLoading] = useState<SocialProvider | null>(null);
    const [error, setError] = useState("");

    const isPublicCustomerLogin = loginMode === "phone";

    const completeAuthNavigation = (data: any) => {
        localStorage.setItem("s360_access", data.access);
        localStorage.setItem("s360_refresh", data.refresh);
        if (data.user) {
            localStorage.setItem("s360_user", JSON.stringify(data.user));
        }
        // Clear stored return URL
        localStorage.removeItem('s360_return_url');
        notifySessionChanged();

        const role = data.user?.role;
        // Admin/vendor always go to their own dashboards
        if (role === "admin" || role === "superadmin") {
            router.push("/admin");
            return;
        }
        if (role === "vendor") {
            if (data.user?.store?.vendor_type === "showroom" || data.user?.vendor_type === "showroom") {
                router.push("/showroom");
            } else {
                router.push("/myprofil");
            }
            return;
        }
        // For customers: if there's a return URL, go back there
        if (nextUrl && nextUrl.startsWith('/')) {
            router.push(nextUrl);
            return;
        }
        router.push("/customer-dashboard");
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            let res: Response;
            let data: any;

            if (isPublicCustomerLogin) {
                // Public customer: login via phone or email with password
                res = await fetch(`${API}/api/auth/public-login/`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ login: loginValue, password }),
                });
                data = await res.json();
            } else {
                // Vendor/Admin: login via email + password (existing endpoint)
                res = await fetch(`${API}/api/auth/login/`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: loginValue, password }),
                });
                data = await res.json();
            }

            if (!res.ok) {
                const msg =
                    data.non_field_errors?.[0] ||
                    data.detail ||
                    data.login?.[0] ||
                    "بيانات الدخول غير صحيحة. حاول مجدداً.";
                setError(msg);
                return;
            }

            completeAuthNavigation(data);
        } catch {
            setError("خطأ في الاتصال بالخادم. تأكد من تشغيل النظام.");
        } finally {
            setLoading(false);
        }
    };

    const handleSocialAuth = (provider: SocialProvider) => {
        setError("");
        setSocialLoading(provider);
        try {
            authApi.startSocialOAuth({
                provider,
                role: isPublicCustomerLogin ? "customer" : "vendor",
                next: isPublicCustomerLogin ? "/customer-dashboard" : "/myprofil",
            });
        } catch (err: any) {
            const message =
                err?.message ||
                "تعذر تسجيل الدخول الاجتماعي حالياً. حاول مرة أخرى.";
            setError(message);
            setSocialLoading(null);
        }
    };

    return (
        <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.20),transparent_34%),linear-gradient(135deg,#eff6ff_0%,#ffffff_46%,#fff7ed_100%)] flex items-center justify-center p-4">
            <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-orange-500 via-blue-600 to-orange-400" />

            <div className="relative w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-7">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-[1.7rem] shadow-2xl shadow-blue-900/15 ring-1 ring-blue-100 mb-4 overflow-hidden">
                        <Image
                            src="/maknna_logo.png"
                            alt="Maknna"
                            width={82}
                            height={82}
                            priority
                            className="h-[82px] w-[82px] object-contain"
                        />
                    </div>
                    <h1 className="text-3xl font-black text-slate-950">Maknna</h1>
                    <p className="text-blue-700 text-sm mt-1 font-bold">Integrated Automotive Platform</p>
                </div>

                {/* Card */}
                <div className="bg-white/90 backdrop-blur-xl border border-blue-100 rounded-3xl p-8 shadow-2xl shadow-blue-900/10">
                    <h2 className="text-xl font-bold text-slate-950 text-center mb-2">تسجيل الدخول</h2>

                    {/* Mode Toggle */}
                    <div className="flex rounded-2xl bg-slate-100 p-1 mb-6 border border-slate-200">
                        <button
                            type="button"
                            onClick={() => { setLoginMode("phone"); setLoginValue(""); }}
                            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2 text-sm font-bold transition-all ${loginMode === "phone" ? "bg-blue-600 text-white shadow-lg shadow-blue-700/20" : "text-slate-500 hover:text-slate-900"}`}
                        >
                            <Phone className="h-4 w-4" />
                            <span>مستخدم عام</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => { setLoginMode("email"); setLoginValue(""); }}
                            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2 text-sm font-bold transition-all ${loginMode === "email" ? "bg-orange-500 text-white shadow-lg shadow-orange-600/20" : "text-slate-500 hover:text-slate-900"}`}
                        >
                            <Store className="h-4 w-4" />
                            <span>تاجر / إدارة</span>
                        </button>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div key={loginMode} className="space-y-4 animate-in fade-in slide-in-from-right-2 duration-300">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">
                                    {loginMode === "phone" ? "رقم الجوال أو البريد الإلكتروني" : "البريد الإلكتروني"}
                                </label>
                                <input
                                    type={loginMode === "email" ? "email" : "text"}
                                    value={loginValue}
                                    onChange={(e) => setLoginValue(e.target.value)}
                                    required
                                    dir="ltr"
                                    className="w-full bg-white border border-slate-200 text-slate-950 placeholder-slate-400 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition shadow-sm"
                                    placeholder={loginMode === "phone" ? "+96891234567 أو example@email.com" : "vendor@email.com"}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">كلمة المرور</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        dir="ltr"
                                        className="w-full bg-white border border-slate-200 text-slate-950 placeholder-slate-400 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition pr-12 shadow-sm"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-700 transition"
                                    >
                                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>
                                {loginMode === "phone" && (
                                    <div className="text-right mt-1.5">
                                        <Link href="/forgot-password" className="text-blue-700 hover:underline text-xs font-bold">
                                            نسيت كلمة المرور؟
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-2xl p-3 text-red-700 text-sm text-center">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 disabled:opacity-70 disabled:cursor-not-allowed text-white font-bold rounded-2xl py-3.5 text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                            {loading ? "جاري الدخول..." : "تسجيل الدخول"}
                        </button>

                        <div className="pt-2 space-y-2">
                            <div className="relative">
                                <div className="h-px bg-slate-200" />
                                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-[11px] text-slate-500 font-bold">
                                    أو المتابعة عبر
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <SocialProviderButton
                                    provider="facebook"
                                    loadingProvider={socialLoading}
                                    disabled={loading}
                                    onClick={handleSocialAuth}
                                />
                                <SocialProviderButton
                                    provider="icloud"
                                    loadingProvider={socialLoading}
                                    disabled={loading}
                                    onClick={handleSocialAuth}
                                />
                                <SocialProviderButton
                                    provider="google"
                                    loadingProvider={socialLoading}
                                    disabled={loading}
                                    onClick={handleSocialAuth}
                                />
                            </div>
                        </div>
                    </form>

                    {/* Info section */}
                    <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
                        <div className="flex items-center gap-3 text-slate-500 text-xs">
                            <Store className="h-4 w-4 text-orange-500 flex-shrink-0" />
                            <span>لوحة تحكم التجار المعتمدين والورش</span>
                        </div>
                        <div className="flex items-center gap-3 text-slate-500 text-xs">
                            <ShieldCheck className="h-4 w-4 text-blue-600 flex-shrink-0" />
                            <span>إدارة متكاملة للموافقات والمدفوعات</span>
                        </div>
                    </div>

                    <p className="text-center text-slate-500 text-xs mt-4">
                        ليس لديك حساب؟{" "}
                        <Link href="/register" className="text-orange-600 hover:underline font-bold">
                            سجّل كتاجر/ورشة
                        </Link>
                        {" · "}
                        <Link href="/public-register" className="text-blue-700 hover:underline font-bold">
                            إنشاء حساب عميل
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
